import { SUPPORTED_LOCALES } from '@floorfile/contracts'
import type { AppLocale, LocaleMeta } from './types'

/**
 * Presentation metadata for every locale the contracts union allows.
 *
 * Labels are **endonyms** — each language's own name for itself. A picker that
 * offers "French" to a francophone is written for the people who do not need
 * it; "Français" is legible to someone who cannot read the current UI, which is
 * the entire population that reaches for a language switcher.
 *
 * `direction` exists so a future RTL language is a data change (D-L4). We ship
 * no RTL locale and no mirrored icons; the field, `<html dir>` and the logical-
 * property lint nudge are the hygiene that keeps that door open.
 *
 * The `satisfies` clause is the enforcement: adding `'es'` to
 * `SUPPORTED_LOCALES` fails the build here until its metadata exists, which is
 * exactly the shape Phase 9 ("adding a language is data") is trying to prove.
 */
export const LOCALES = {
  // English stays the UNqualified tag on purpose (owner decision, 2026-08-01).
  // Pinning it to `en-CA` would silently re-render every date and time for
  // every user the app has today (`a.m.` for `AM`, `2026-08-01` for
  // `8/1/2026`) to satisfy a pairing only the French half actually needs — a
  // visible change nobody asked for, in the phase whose whole point is that
  // English output is byte-identical so the e2e suite's 355 text locators keep
  // working as a free regression net.
  en: { code: 'en', label: 'English', direction: 'ltr', intlTag: 'en' },
  /**
   * **French CANADIAN — owner decision, 2026-08-01, reaffirmed the same day
   * after the vocabulary audit below.**
   *
   * This field is the seam between the two-letter code the DATABASE stores and
   * the BCP-47 tag `Intl` needs, and the two genuinely differ here. `Intl`
   * reads a bare `'fr'` as metropolitan French: 01:30 renders `1:30` and
   * 2 August renders `02/08/2026`. French Canadian renders `1 h 30` and
   * `2026-08-02`. Handing the storage code straight to `Intl` would have
   * shipped the wrong French while looking correct in every English review.
   *
   * **Why Canadian and not generic, recorded so it is not re-litigated.** The
   * catalogue vocabulary is already Québécois and was before this workstream
   * started: `Clavardage` for chat came from the deleted `lib/localization.ts`
   * table, and `NIP` (16 places) and `Courriel` follow it. Metropolitan French
   * says `Chat`/`Discussion`, `code PIN`, `e-mail`. Generic `fr` formatting
   * with Québécois words is the one combination wrong for both audiences —
   * so the tag matches the words rather than the words being rewritten.
   * Commercially this is also the market: FloorFile is a plant OS, and the
   * French-speaking North American manufacturing base is Québec, where
   * French-language workplace UI is a legal requirement rather than a
   * preference.
   *
   * Adding metropolitan French later is additive, not a reversal: a new
   * `SUPPORTED_LOCALES` entry, an entry here, and a catalogue.
   */
  fr: { code: 'fr', label: 'Français (Canada)', direction: 'ltr', intlTag: 'fr-CA' },
} as const satisfies Record<AppLocale, LocaleMeta>

export const LOCALE_LIST: readonly LocaleMeta[] = SUPPORTED_LOCALES.map((code) => LOCALES[code])

export function localeMeta(code: AppLocale): LocaleMeta {
  return LOCALES[code]
}

/**
 * The tag every `Intl` constructor gets.
 *
 * Never pass the stored code straight to `Intl`. The two-letter code is a
 * *storage* value; the tag is a *formatting* value, and for the pilot language
 * they genuinely differ — `'fr'` is metropolitan French and renders times as
 * `1:30`, where French Canadian renders `01 h 30`. Accepting an unknown string
 * (a stale `localStorage` seed, a hand-edited row) falls back to the string
 * itself, so `Intl`'s own resolution handles it rather than this throwing.
 */
export function intlTag(locale: string): string {
  return (LOCALES as Record<string, LocaleMeta | undefined>)[locale]?.intlTag ?? locale
}

/** Narrow an untrusted string (a `localStorage` seed, a URL) to a locale. */
export function isAppLocale(value: unknown): value is AppLocale {
  return typeof value === 'string' && (SUPPORTED_LOCALES as readonly string[]).includes(value)
}
